import React, { useState } from "react";
import axios from "axios";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { useNavigate } from "react-router-dom";
import { GoAlertFill } from "react-icons/go";
import Spinner from "../Assets/auth_spinner.svg";
import "../styles/LoginPage.css";

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [passwordError, setPasswordError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);

  useGSAP(() => {
    gsap.from(".floating-label", {
      duration: 0.8,
      y: 50,
      opacity: 0,
      stagger: 0.1,
      ease: "power2.out",
    });

    gsap.from("#submitBtn", {
      duration: 1,
      scale: 0.5,
      opacity: 0,
      delay: 0.3,
      ease: "power2.out",
    });
  }, []);

  useGSAP(() => {
      gsap.from("#successModal", {
        duration: 0.8,
        y: 50,
        opacity: 0,
        stagger: 0.1,
        ease: "power2.out",
      });
    }, [success]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = (field, value) => {
    if (field === "email") {
      setEmail(value);
    } else {
      setPassword(value);
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFocus = (field) => {
    // Clear error when field gets focus
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const resetPassword = async () => {
    try {
      setLoading(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/auth/forgot-password`,
        { email }
      );

      if (response?.data?.success) {
        setSuccess(true);
      }
    } catch (error) {
      setPasswordError(
        error?.response?.data?.message ||
          "Unexpected error occurred, please try again"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched({ email: true, password: true });

    let hasError = false;
    const newErrors = { email: "", password: "" };

    if (!email.trim()) {
      newErrors.email = "Required";
      hasError = true;
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email format";
      hasError = true;
    }

    setErrors(newErrors);
    if (!hasError) {
      resetPassword();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      {success && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            id="successModal"
            className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6"
          >
            {/* Modal Content */}
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Password Updated
            </h2>
            <h4 className="text-sm text-gray-600 mb-6">
              Your password has been successfully changed. A new temporary
              password has been sent to your registered email. Please log in
              using that password and update it in your account settings.
            </h4>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => navigate("/signin")}
                className="bg-[#1E3557] text-white px-4 py-2 rounded-md hover:shadow-md focus:outline-none"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 w-full h-screen">
        {/* Left Side */}
        <div
          className="h-screen relative bg-cover bg-center bg-no-repeat flex items-center justify-center"
          style={{ backgroundImage: "url('/Assets/11.jpg')" }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <h1 className="text-4xl font-bold text-white z-10">
            Welcome To SocraticAli Consultants
          </h1>
        </div>

        {/* Right Side - Form */}
        <div className="flex items-center justify-center h-screen pr-10">
          <div className="p-8 bg-white shadow-lg rounded-lg h-auto w-[60%] mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-2 text-start">
              Reset Password
            </h1>
            <p className="text-sm text-gray-500 mb-6 text-start">
              SocraticAli Consultant Portal
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 w-full">
              {/* Email */}
              <div className="floating-label">
                <input
                  type="email"
                  id="email"
                  placeholder=" "
                  className={`focus:ring-1 focus:ring-[#457B9D] ${
                    touched.email && errors.email ? "input-error" : ""
                  }`}
                  value={email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  onFocus={() => handleFocus("email")}
                />
                <label htmlFor="email">Email</label>
                <p
                  className={`helper-text ${
                    touched.email && errors.email ? "hidden" : ""
                  }`}
                >
                  Please enter your email
                </p>
                {touched.email && errors.email && (
                  <p className="error-message visible">{errors.email}</p>
                )}
              </div>

              {passwordError && (
                <div className="flex items-center gap-x-2 bg-red-100 border border-red-700 text-red-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
                  <GoAlertFill className="size-5" />
                  <p>{passwordError}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                id="submitBtn"
                className="w-full bg-[#1E3557] text-white px-4 py-2 rounded-md font-semibold relative"
              >
                Reset Password
                {loading && (
                  <img
                    src={Spinner}
                    alt=""
                    className="size-6 absolute top-2 right-5"
                  />
                )}
              </button>
            </form>
            <button onClick={()=> navigate('/signin')} className="flex justify-center my-4 mx-auto text-[#457B9D] font-medium">
              Back to Sign In
            </button>

            {/* Links */}
            <div className="flex justify-center text-sm text-gray-500 mt-4">
              <span>
                Don't Have An Account Yet?{" "}
                <button
                  className="text-[#457B9D] font-semibold"
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
